import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ArrowRight, Check, ChevronDown, ChevronUp, Circle } from "lucide-mui";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { OnboardingStepKey, OrgDashboardTenant } from "../../../api/client.ts";
import { pathForTenant } from "../../../contexts/TenantContext.tsx";
import { useUiPreference } from "../../../hooks/useUiPreference.ts";
import Panel from "../../Panel.tsx";
import { TenantAvatar } from "../../TenantAvatar.tsx";

interface StepMeta {
  label: string;
  /** The "Next" button, when it is the next step. */
  action: string;
  /** The matrix column header. */
  short: string;
  why: string;
  /** Where the step is done; `org` steps live on the org's integrations page. */
  where: "org" | ((slug: string) => string);
  /** The onboarding agent does (or drafts) it, so it waits for Claude. */
  byAgent?: boolean;
}

// In the order the backend sends them: Claude first, since it runs the
// onboarding agent that reads the tenant's website into the profile and logo
// and drafts its workflows.
export const ONBOARDING_STEPS: Record<OnboardingStepKey, StepMeta> = {
  claude: {
    label: "Claude connected",
    action: "Connect Claude",
    short: "Claude",
    why: "Runs the onboarding agent and every agent after it, on your organization's Claude plan",
    where: "org",
  },
  profile: {
    label: "Company profile",
    action: "Run the onboarding agent",
    short: "Profile",
    why: "The onboarding agent drafts it from your website; every agent reads it",
    where: (slug) => pathForTenant(slug, "/settings"),
    byAgent: true,
  },
  logo: {
    label: "Logo",
    action: "Run the onboarding agent",
    short: "Logo",
    why: "The onboarding agent fetches it from your website",
    where: (slug) => pathForTenant(slug, "/settings"),
    byAgent: true,
  },
  signal_workflow: {
    label: "Signal workflow live",
    action: "Switch on a signal workflow",
    short: "Signals",
    why: "Watches news, job ads and filings for buying signals. The onboarding agent drafts it paused",
    where: (slug) => pathForTenant(slug, "/workflows?trigger=signal"),
    byAgent: true,
  },
  agent_workflow: {
    label: "Prospecting agent live",
    action: "Switch on the prospecting agent",
    short: "Agent",
    why: "Works every prospect order placed for the tenant. The onboarding agent drafts it paused",
    where: (slug) => pathForTenant(slug, "/workflows?trigger=prospect_order"),
    byAgent: true,
  },
  crm: {
    label: "CRM connected",
    action: "Connect your CRM",
    short: "CRM",
    why: "Your companies, contacts and deals sync in",
    where: "org",
  },
  first_decision: {
    label: "First prospect reviewed",
    action: "Review your first prospect",
    short: "Review",
    why: "Your decisions teach the agents what a good prospect is",
    where: (slug) => pathForTenant(slug, "/prospects/inbox"),
  },
};

const INTEGRATIONS = "/organization/integrations";

function stepLink(key: OnboardingStepKey, slug: string, canManageOrg: boolean): string | undefined {
  const where = ONBOARDING_STEPS[key].where;
  if (where === "org") return canManageOrg ? INTEGRATIONS : undefined;
  return where(slug);
}

function progressOf(tenant: OrgDashboardTenant): { done: number; total: number } {
  const steps = tenant.onboarding ?? [];
  return { done: steps.filter((s) => s.done).length, total: steps.length };
}

/** A small ring with the share done in its middle. */
export function ProgressRing({ done, total, size = 56 }: { done: number; total: number; size?: number }) {
  const value = total ? (done / total) * 100 : 0;
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{ color: "action.hover", position: "absolute", inset: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={4}
        color={done === total ? "success" : "primary"}
        sx={{ position: "absolute", inset: 0, "& circle": { strokeLinecap: "round" } }}
      />
      <Typography
        component="span"
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          fontSize: size > 40 ? 14 : 10,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Math.round(value)}%
      </Typography>
    </Box>
  );
}

interface OnboardingPanelProps {
  tenants: OrgDashboardTenant[];
  /** May open the org's integrations page (Claude and CRM are set up there). */
  canManageOrg: boolean;
}

/**
 * How far the org's own tenants are from agents prospecting for them: one
 * tenant as a checklist with its next step, several as a matrix (folded to a
 * per-step summary above a handful of tenants). Gone once every step is done.
 */
export default function OnboardingPanel({ tenants, canManageOrg }: OnboardingPanelProps) {
  const owned = tenants.filter((t) => t.onboarding);
  const unfinished = owned.filter((t) => progressOf(t).done < progressOf(t).total);
  if (unfinished.length === 0) return null;
  return owned.length === 1 ? (
    <SingleTenant tenant={owned[0]} canManageOrg={canManageOrg} />
  ) : (
    <ManyTenants tenants={owned} canManageOrg={canManageOrg} />
  );
}

function SingleTenant({ tenant, canManageOrg }: { tenant: OrgDashboardTenant; canManageOrg: boolean }) {
  const steps = tenant.onboarding ?? [];
  const { done, total } = progressOf(tenant);
  const claudeReady = steps.find((s) => s.key === "claude")?.done ?? true;
  // Without Claude the agent's steps wait: the next step is connecting it, or asking for it.
  const next = steps.find(
    (s) => !s.done && (claudeReady || !ONBOARDING_STEPS[s.key].byAgent) && stepLink(s.key, tenant.slug, canManageOrg),
  );
  const nextLink = next && stepLink(next.key, tenant.slug, canManageOrg);
  return (
    <Panel>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, mb: 2 }}>
        <ProgressRing done={done} total={total} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h2">
            Get {tenant.name} ready for the agents
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {done} of {total} steps done. Each one makes the prospects the agents bring you sharper.
          </Typography>
        </Box>
        {next && nextLink && (
          <Button component={Link} to={nextLink} variant="contained" endIcon={<ArrowRight />} sx={{ flexShrink: 0 }}>
            {ONBOARDING_STEPS[next.key].action}
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
        }}
      >
        {steps.map((step) => (
          <StepTile
            key={step.key}
            stepKey={step.key}
            done={step.done}
            waitsForClaude={!claudeReady && !!ONBOARDING_STEPS[step.key].byAgent}
            to={step.done ? undefined : stepLink(step.key, tenant.slug, canManageOrg)}
          />
        ))}
      </Box>
    </Panel>
  );
}

function StepTile({
  stepKey,
  done,
  waitsForClaude,
  to,
}: {
  stepKey: OnboardingStepKey;
  done: boolean;
  waitsForClaude: boolean;
  to?: string;
}) {
  const meta = ONBOARDING_STEPS[stepKey];
  const orgOnly = !done && !to && meta.where === "org";
  const caption = orgOnly
    ? "Ask an organization admin to set this up"
    : !done && waitsForClaude
      ? "Connect Claude first: the onboarding agent does this for you"
      : meta.why;
  return (
    <ButtonBase
      component={to ? Link : "div"}
      to={to}
      disabled={!to}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        gap: 1.25,
        p: 1.25,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: done ? "action.hover" : "transparent",
        "&:hover": to ? { borderColor: "primary.main" } : undefined,
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: done ? "success.main" : "transparent",
          color: done ? "success.contrastText" : "text.disabled",
          border: done ? 0 : 1.5,
          borderColor: "divider",
          "& svg": { fontSize: 14 },
        }}
      >
        {done && <Check />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: done ? "text.secondary" : "text.primary" }}>
          {meta.label}
        </Typography>
        <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

/** Above this many tenants the matrix folds away behind a per-step summary. */
const COLLAPSE_ABOVE = 3;

const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";

function ManyTenants({ tenants, canManageOrg }: { tenants: OrgDashboardTenant[]; canManageOrg: boolean }) {
  const collapsible = tenants.length > COLLAPSE_ABOVE;
  const [expandedPref, setExpanded] = useUiPreference("dashboard.onboarding.expanded", false, isBoolean);
  const expanded = !collapsible || expandedPref;
  const keys = (tenants[0].onboarding ?? []).map((s) => s.key);
  const totals = tenants.reduce(
    (acc, t) => {
      const p = progressOf(t);
      return { done: acc.done + p.done, total: acc.total + p.total };
    },
    { done: 0, total: 0 },
  );
  const share = totals.total ? Math.round((totals.done / totals.total) * 100) : 0;
  const ready = tenants.filter((t) => progressOf(t).done === progressOf(t).total).length;
  // Gaps on top: the least set-up tenants first.
  const sorted = useMemo(
    () =>
      [...tenants].sort((a, b) => {
        const pa = progressOf(a);
        const pb = progressOf(b);
        return pa.done / (pa.total || 1) - pb.done / (pb.total || 1) || a.name.localeCompare(b.name);
      }),
    [tenants],
  );
  return (
    <Panel
      title="Onboarding"
      subtitle={
        collapsible
          ? `${share}% done · ${ready} of ${tenants.length} tenants fully set up`
          : `${share}% done across ${tenants.length} tenants. Open a gap to fill it in.`
      }
      action={
        collapsible && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ChevronUp /> : <ChevronDown />}
            aria-expanded={expanded}
          >
            {expanded ? "Hide tenants" : "Show tenants"}
          </Button>
        )
      }
    >
      <LinearProgress
        variant="determinate"
        value={share}
        color={share === 100 ? "success" : "primary"}
        sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", mb: collapsible ? 1.5 : 2 }}
      />
      {collapsible && <StepSummary keys={keys} tenants={tenants} />}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pt: collapsible ? 2 : 0 }}>
          <TenantMatrix keys={keys} tenants={sorted} canManageOrg={canManageOrg} />
        </Box>
      </Collapse>
    </Panel>
  );
}

/** Per step, how many tenants have it done: the many-tenant matrix in one line. */
function StepSummary({ keys, tenants }: { keys: OnboardingStepKey[]; tenants: OrgDashboardTenant[] }) {
  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
      {keys.map((k) => {
        const done = tenants.filter((t) => t.onboarding?.find((s) => s.key === k)?.done).length;
        const all = done === tenants.length;
        return (
          <Tooltip key={k} title={`${ONBOARDING_STEPS[k].label}: ${done} of ${tenants.length} tenants`}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                alignItems: "center",
                px: 1,
                py: 0.5,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: all ? "action.hover" : "transparent",
              }}
            >
              <Box
                component="span"
                sx={{ display: "grid", color: all ? "success.main" : "text.disabled", "& svg": { fontSize: all ? 14 : 8 } }}
              >
                {all ? <Check /> : <Circle />}
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: all ? "text.secondary" : "text.primary" }}>
                {ONBOARDING_STEPS[k].short}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                {done}/{tenants.length}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function TenantMatrix({
  keys,
  tenants,
  canManageOrg,
}: {
  keys: OnboardingStepKey[];
  tenants: OrgDashboardTenant[];
  canManageOrg: boolean;
}) {
  const cols = `minmax(160px, 1.6fr) repeat(${keys.length}, minmax(44px, 1fr)) 48px`;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", rowGap: 0.5, minWidth: 560 }}>
        <Box />
        {keys.map((k) => (
          <Tooltip key={k} title={ONBOARDING_STEPS[k].label}>
            <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", fontWeight: 600 }}>
              {ONBOARDING_STEPS[k].short}
            </Typography>
          </Tooltip>
        ))}
        <Box />
        {tenants.map((tenant) => {
          const { done, total } = progressOf(tenant);
          return [
            <Stack key={`${tenant.id}-name`} direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, py: 0.5 }}>
              <TenantAvatar name={tenant.name} logoUrl={tenant.logo_url} size={22} />
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {tenant.name}
              </Typography>
            </Stack>,
            ...(tenant.onboarding ?? []).map((step) => {
              const to = step.done ? undefined : stepLink(step.key, tenant.slug, canManageOrg);
              return (
                <Box key={`${tenant.id}-${step.key}`} sx={{ display: "grid", placeItems: "center" }}>
                  <Tooltip title={`${ONBOARDING_STEPS[step.key].label}: ${step.done ? "done" : "to do"}`}>
                    <ButtonBase
                      component={to ? Link : "span"}
                      to={to}
                      disabled={!to}
                      aria-label={`${tenant.name}: ${ONBOARDING_STEPS[step.key].label}`}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: step.done ? "success.main" : "text.disabled",
                        "& svg": { fontSize: step.done ? 16 : 10 },
                        "&:hover": to ? { bgcolor: "action.hover", color: "primary.main" } : undefined,
                      }}
                    >
                      {step.done ? <Check /> : <Circle />}
                    </ButtonBase>
                  </Tooltip>
                </Box>
              );
            }),
            <Box key={`${tenant.id}-ring`} sx={{ display: "grid", placeItems: "center" }}>
              <ProgressRing done={done} total={total} size={30} />
            </Box>,
          ];
        })}
      </Box>
    </Box>
  );
}
